import React, {useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {RootState} from '../../store';
import ServiceCard from './services/components/ServiceCard';
import ServiceDetailsModal from './services/components/ServiceDetailsModal';
import ServicesPagination from './services/components/ServicesPagination';
import ServicesWarningIcon from './services/components/ServicesWarningIcon';
import ServicesCategoryBar from './services/components/ServicesCategoryBar';
import ServicesEmptyState from './services/components/ServicesEmptyState';
import ServicesHero from './services/components/ServicesHero';
import ServicesToolbar from './services/components/ServicesToolbar';
import {
  SERVICE_CATEGORIES,
  SERVICE_PRICE_RANGE_OPTIONS,
  SERVICE_SORT_OPTIONS,
  SERVICES_PER_PAGE,
  ServiceCategoryId,
  ServiceListing,
  ServicePriceRangeId,
  ServiceSortId,
} from './services/constants';
import {formatServicesResultRange} from './services/servicesPagination';
import {servicesApi} from '../../services/servicesApi';
import {analyticsApi, RecordServiceClickPayload} from '../../services/analyticsApi';
import styles from './services/Services.module.css';

const Services: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategoryId>('all');
  const [priceRange, setPriceRange] = useState<ServicePriceRangeId>('all');
  const [sortBy, setSortBy] = useState<ServiceSortId>('recommended');
  const [showFilters, setShowFilters] = useState(false);
  const [services, setServices] = useState<ServiceListing[]>([]);
  const [page, setPage] = useState(1);
  const [totalServices, setTotalServices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedService, setSelectedService] = useState<ServiceListing | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const normalizedSearchQuery = searchQuery.trim();

  const handleSearchQueryChange = React.useCallback((nextSearchQuery: string) => {
    setSearchQuery(nextSearchQuery);
    setPage(1);
  }, []);

  const handleSelectCategory = React.useCallback((nextCategory: ServiceCategoryId) => {
    setSelectedCategory(nextCategory);
    if (nextCategory === 'college') {
      setPriceRange('all');
    }
    setPage(1);
  }, []);

  const handleSortChange = React.useCallback((nextSortBy: ServiceSortId) => {
    setSortBy(nextSortBy);
    setPage(1);
  }, []);

  const handleToggleFilters = React.useCallback(() => {
    setShowFilters((currentValue) => !currentValue);
  }, []);

  const handleCloseFilters = React.useCallback(() => {
    setShowFilters(false);
  }, []);

  const handleApplyFilters = React.useCallback((nextFilters: {
    category: ServiceCategoryId;
    priceRange: ServicePriceRangeId;
  }) => {
    setSelectedCategory(nextFilters.category);
    setPriceRange(nextFilters.category === 'college' ? 'all' : nextFilters.priceRange);
    setPage(1);
    setShowFilters(false);
  }, []);

  const handleClearStructuredFilters = React.useCallback(() => {
    setSelectedCategory('all');
    setPriceRange('all');
    setPage(1);
    setShowFilters(false);
  }, []);

  React.useEffect(() => {
    if (!token) {
      setServices([]);
      setTotalServices(0);
      setTotalPages(0);
      setError('You must be signed in to view services.');
      setLoading(false);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();
    const requestDelay = window.setTimeout(() => {
      const loadServices = async () => {
        setLoading(true);
        setError(null);

        try {
          const response = await servicesApi.getServices(
            token,
            {
              keyword: normalizedSearchQuery || undefined,
              category: selectedCategory,
              priceRange,
              sort: sortBy,
              page,
              limit: SERVICES_PER_PAGE,
            },
            controller.signal
          );

          if (!isMounted) {
            return;
          }

          setServices(response.services);
          setTotalServices(response.total);
          setTotalPages(response.totalPages);

          if (response.page !== page) {
            setPage(response.page);
          }
        } catch (loadError) {
          if (axios.isAxiosError(loadError) && loadError.code === 'ERR_CANCELED') {
            return;
          }

          if (!isMounted) {
            return;
          }

          setServices([]);
          setTotalServices(0);
          setTotalPages(0);
          setError(loadError instanceof Error ? loadError.message : 'Failed to load services');
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };

      void loadServices();
    }, 250);

    return () => {
      isMounted = false;
      window.clearTimeout(requestDelay);
      controller.abort();
    };
  }, [normalizedSearchQuery, page, priceRange, retryCount, selectedCategory, sortBy, token]);

  const displayedServices = React.useMemo(() => services.map((service) => (
    currentUser && service.providerUserId === currentUser.id
      ? {
        ...service,
        providerProfilePicture: currentUser.avatar || undefined,
      }
      : service
  )), [currentUser, services]);

  const activeFilterCount = (
    (selectedCategory !== 'all' ? 1 : 0) +
    (selectedCategory !== 'college' && priceRange !== 'all' ? 1 : 0)
  );
  const hasActiveFilters = (
    searchQuery.trim().length > 0 ||
    activeFilterCount > 0
  );
  const resultSummary = formatServicesResultRange(
    totalServices,
    page,
    SERVICES_PER_PAGE,
    displayedServices.length
  );

  const trackServiceClick = React.useCallback((
    service: ServiceListing,
    action: NonNullable<RecordServiceClickPayload['action']>
  ) => {
    if (!token) {
      return;
    }

    analyticsApi.recordServiceClick(token, {
      ownerId: service.providerUserId,
      serviceId: service._id,
      serviceKind: service.serviceKind,
      action,
    }).catch(() => undefined);
  }, [token]);

  const handleBookService = React.useCallback((service: ServiceListing) => {
    trackServiceClick(service, 'book');
    alert(`Booking request for: ${service.title}\n\nIn production, this would:\n1. Open booking calendar\n2. Show available time slots\n3. Process payment\n4. Send confirmation email`);
  }, [trackServiceClick]);

  const handleContactProvider = React.useCallback((service: ServiceListing) => {
    trackServiceClick(service, 'message');
    navigate(`/messages?recipientId=${service.providerUserId}`);
  }, [navigate, trackServiceClick]);

  const handleLearnMore = React.useCallback((service: ServiceListing) => {
    trackServiceClick(service, 'learn_more');
  }, [trackServiceClick]);

  const handleViewDetails = React.useCallback((service: ServiceListing) => {
    trackServiceClick(service, 'learn_more');
    setSelectedService(service);
  }, [trackServiceClick]);

  const handleClearFilters = React.useCallback(() => {
    setSearchQuery('');
    setSelectedCategory('all');
    setPriceRange('all');
    setPage(1);
  }, []);

  const handlePageChange = React.useCallback((nextPage: number) => {
    setPage(Math.min(Math.max(1, nextPage), Math.max(1, totalPages)));

    window.requestAnimationFrame(() => {
      const contentElement = contentRef.current;

      if (!contentElement) {
        return;
      }

      const navbarHeight = document.querySelector('.navbar')?.getBoundingClientRect().height || 0;
      const contentTop = contentElement.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: Math.max(0, contentTop - navbarHeight - 16),
        behavior: 'smooth',
      });
    });
  }, [totalPages]);

  return (
    <div className={styles.pageRoot}>
      <ServicesHero
        categories={SERVICE_CATEGORIES}
        searchQuery={searchQuery}
        selectedCategory={selectedCategory}
        onSearchQueryChange={handleSearchQueryChange}
        onSelectCategory={handleSelectCategory}
      />

      <ServicesCategoryBar
        categories={SERVICE_CATEGORIES}
        selectedCategory={selectedCategory}
        onSelectCategory={handleSelectCategory}
      />

      <div ref={contentRef} className={`container ${styles.content}`}>
        {!loading && !error ? (
          <ServicesToolbar
            categories={SERVICE_CATEGORIES}
            resultSummary={resultSummary}
            selectedCategory={selectedCategory}
            priceRange={priceRange}
            priceRangeOptions={SERVICE_PRICE_RANGE_OPTIONS}
            sortBy={sortBy}
            sortOptions={SERVICE_SORT_OPTIONS}
            showFilters={showFilters}
            activeFilterCount={activeFilterCount}
            onSortChange={handleSortChange}
            onToggleFilters={handleToggleFilters}
            onCloseFilters={handleCloseFilters}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearStructuredFilters}
          />
        ) : null}

        {loading ? (
          <div className={styles.stateCard}>
            <div className={styles.loadingSpinner} />
            <h3 className={styles.stateTitle}>Loading services</h3>
            <p className={styles.stateDescription}>
              We&apos;re fetching the latest services and college opportunities shared on profiles.
            </p>
          </div>
        ) : error ? (
          <div className={styles.stateCard}>
            <ServicesWarningIcon />
            <h3 className={styles.stateTitle}>Unable to load services</h3>
            <p className={styles.stateDescription}>{error}</p>
            <button
              type="button"
              onClick={() => setRetryCount((currentRetryCount) => currentRetryCount + 1)}
              className={styles.retryButton}
            >
              Try Again
            </button>
          </div>
        ) : totalServices === 0 && !hasActiveFilters ? (
          <div className={styles.stateCard}>
            <div className={styles.stateIcon}>🗂️</div>
            <h3 className={styles.stateTitle}>No services available yet</h3>
            <p className={styles.stateDescription}>
              There are no profile-owned services or college opportunities available yet. Ask providers to add them on their Profile page.
            </p>
          </div>
        ) : displayedServices.length > 0 ? (
          <>
            <div className={styles.grid}>
              {displayedServices.map((service) => (
                <ServiceCard
                  key={service._id}
                  service={service}
                  onContactProvider={handleContactProvider}
                  onBookService={handleBookService}
                  onLearnMore={handleLearnMore}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>

            <ServicesPagination
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <ServicesEmptyState
            onClearFilters={handleClearFilters}
          />
        )}
      </div>

      <ServiceDetailsModal
        isOpen={Boolean(selectedService)}
        service={selectedService}
        onClose={() => setSelectedService(null)}
        onBookService={handleBookService}
        onContactProvider={handleContactProvider}
        onLearnMore={handleLearnMore}
      />
    </div>
  );
};

export default Services;
